import { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import { toast } from "react-toastify";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import brandStyles from "../Brand/Brand.module.css";
import styles from "./PolicyTemplate.module.css";
import "../../components/mcrbTableTheme.css";
import { renderPolicyPreview, STATUS_OPTIONS, SampleVariable } from "./policyTokens";

const { baseNodeapiUrl } = config;
const termsApiUrl = `${baseNodeapiUrl}/terms`;

const AddPolicyTemplate = () => {
    const params: any = useParams();
    const history = useHistory();
    const isEdit = Boolean(params?.id);

    const [content, setContent] = useState("");
    const [status, setStatus] = useState("draft");
    const [sampleVariables, setSampleVariables] = useState<SampleVariable[]>([{ key: "", value: "" }]);
    const [errors, setErrors] = useState({ content: false });
    const [loading, setLoading] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const fetchTemplate = async (id: any) => {
        setLoadingDetail(true);
        try {
            const { data } = await axios({
                url: `${termsApiUrl}/${id}`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
            });
            const item = data?.data || data;
            setContent(item?.content || "");
            setStatus(item?.status || "draft");
            setSampleVariables(item?.sample_variables?.length ? item.sample_variables : [{ key: "", value: "" }]);
        } catch (error) {
            console.log("error", error);
            toast.error("Error loading terms template");
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => {
        if (isEdit) {
            fetchTemplate(params.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.id]);

    function updateVariable(index: number, field: "key" | "value", value: string) {
        setSampleVariables((prev) => prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)));
    }

    function addVariable() {
        setSampleVariables((prev) => [...prev, { key: "", value: "" }]);
    }

    function removeVariable(index: number) {
        setSampleVariables((prev) => prev.filter((_, i) => i !== index));
    }

    function validate() {
        const nextErrors = { content: !content || content === "<br>" };
        setErrors(nextErrors);
        return !nextErrors.content;
    }

    async function saveTemplate() {
        if (!validate()) {
            toast.error("Please fill the required fields!");
            return;
        }
        setLoading(true);
        try {
            const cleanVariables = sampleVariables.filter((v) => v.key.trim());
            await axios({
                url: isEdit ? `${termsApiUrl}/${params.id}` : `${termsApiUrl}/create`,
                method: isEdit ? "PUT" : "POST",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
                data: {
                    content,
                    status,
                    sample_variables: cleanVariables,
                },
            });
            toast.success(isEdit ? "Terms template updated!" : "Terms template created!");
            history.push("/policytemplatelist");
        } catch (error) {
            console.log("error", error);
            toast.error("Something went wrong!");
        } finally {
            setLoading(false);
        }
    }

    const editorTokens = sampleVariables.filter((v) => v.key.trim()).map((v) => ({ label: v.key, token: v.key }));

    return (
        <ThemeMaker>
            <div className={`${brandStyles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>{isEdit ? "Edit Terms Template" : "Add Terms Template"}</span>
                        <button type="button" className={brandStyles.primaryBtn} onClick={() => history.push("/policytemplatelist")}>
                            Terms Template List
                        </button>
                    </div>

                    <div className={brandStyles.bodyArea} style={{ minHeight: "70vh" }}>
                        {loadingDetail ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>Loading...</div>
                        ) : (
                            <div className={styles.formGrid}>
                                <div className={styles.varsBlock}>
                                    <div className={styles.varsHeader}>
                                        <span className={styles.varsTitle}>Sample Variables</span>
                                        <button type="button" className={styles.addVarBtn} onClick={addVariable}>
                                            <AddCircleOutlineIcon style={{ fontSize: 16 }} /> Add Variable
                                        </button>
                                    </div>
                                    <p className={styles.varsHint}>
                                        Define the placeholders this template needs (e.g. company_name, user_name) with a sample value. Use "Insert Token" in the editor below to drop them into the content as {"{{key}}"}.
                                    </p>
                                    {sampleVariables.map((v, index) => (
                                        <div className={styles.varRow} key={index}>
                                            <input
                                                type="text"
                                                className={`${styles.varInput} ${styles.varKeyInput}`}
                                                placeholder="key (e.g. company_name)"
                                                value={v.key}
                                                onChange={(e) => updateVariable(index, "key", e.target.value.replace(/\s+/g, "_"))}
                                            />
                                            <input
                                                type="text"
                                                className={styles.varInput}
                                                placeholder="Sample value (e.g. XpressBaazaar Pvt. Ltd.)"
                                                value={v.value}
                                                onChange={(e) => updateVariable(index, "value", e.target.value)}
                                            />
                                            <button type="button" className={styles.removeVarBtn} onClick={() => removeVariable(index)}>
                                                <DeleteIcon style={{ fontSize: 18 }} />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className={styles.editorField}>
                                    <label className={brandStyles.label}>
                                        Content<span className={brandStyles.required}>*</span>
                                    </label>
                                    {errors.content ? <span className={brandStyles.errorText}>This field is required !</span> : null}
                                    <RichTextEditor
                                        value={content}
                                        onChange={setContent}
                                        tokens={editorTokens}
                                        placeholder="Write the terms & conditions content here..."
                                    />
                                </div>

                                <div className={brandStyles.field}>
                                    <label className={brandStyles.label}>Status</label>
                                    <select
                                        className={brandStyles.input}
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        style={{ maxWidth: "220px" }}
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option value={s} key={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.previewToggleRow}>
                                    <button type="button" className={styles.previewToggleBtn} onClick={() => setShowPreview((p) => !p)}>
                                        {showPreview ? "Hide Preview" : "Show Preview"}
                                    </button>
                                </div>

                                {showPreview && (
                                    <div className={styles.previewPane}>
                                        <div className={styles.previewPaneHeader}>Preview (with sample values)</div>
                                        <div
                                            className={styles.previewPaneBody}
                                            dangerouslySetInnerHTML={{ __html: renderPolicyPreview(content, sampleVariables) }}
                                        />
                                    </div>
                                )}

                                <div>
                                    <button type="submit" className={brandStyles.saveBtn} onClick={saveTemplate} disabled={loading}>
                                        {loading ? "Saving..." : isEdit ? "Update" : "Save"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ThemeMaker>
    );
};

export default AddPolicyTemplate;
